import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { api } from '../../lib/api';
import { Siswa, TagihanSiswa, TransaksiPembayaran, TahunAjaran } from '../../types';
import { KwitansiModal } from '../../components/keuangan/KwitansiModal';
import { Modal } from '../../components/ui/Modal';
import { TableSkeleton } from '../../components/ui/Skeleton';
import {
  CreditCard,
  Search,
  User,
  CheckCircle2,
  AlertCircle,
  Clock,
  Printer,
  Ban,
  Wallet,
  ArrowRight,
  ShieldAlert,
  Calendar,
  ListChecks,
} from 'lucide-react';

const URUTAN_BULAN_AJARAN = [
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
];
const NAMA_BULAN_ID = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
];

// Mencocokkan isi kolom bulan tagihan ("Juli", "Jul 2026", atau "2026-07") dengan nama bulan
const cocokBulan = (raw: string | null | undefined, namaBulan: string) => {
  if (!raw) return false;
  const s = String(raw).toLowerCase();
  if (s.includes(namaBulan.toLowerCase().slice(0, 3))) return true;
  const m = s.match(/-(\d{2})(?:-|$)/);
  return Boolean(m && Number(m[1]) === NAMA_BULAN_ID.indexOf(namaBulan) + 1);
};

export function PembayaranPage() {
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const { success, error, warning } = useToast();
  const queryClient = useQueryClient();

  const canEdit = hasPermission('keuangan', 'ubah') || hasPermission('keuangan', 'tambah');

  // Search Siswa State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSiswaId, setSelectedSiswaId] = useState<number | null>(null);

  // Kwitansi Modal State
  const [selectedTxId, setSelectedTxId] = useState<number | null>(null);

  // Form Bayar Modal State
  const [bayarModalOpen, setBayarModalOpen] = useState(false);
  const [selectedTagihan, setSelectedTagihan] = useState<TagihanSiswa | null>(null);
  const [bayarForm, setBayarForm] = useState({
    jumlah_bayar: 0,
    metode: 'Tunai' as 'Tunai' | 'Transfer',
    tanggal_bayar: new Date().toISOString().slice(0, 10),
    catatan: '',
  });

  // Void Modal State
  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [voidTx, setVoidTx] = useState<TransaksiPembayaran | null>(null);
  const [alasanBatal, setAlasanBatal] = useState('');

  // Pembayaran gabungan (centang beberapa tagihan, satu kwitansi)
  // pilihan: tagihan_id -> nominal yang akan dibayar
  const [pilihan, setPilihan] = useState<Record<number, number>>({});
  const [multiModalOpen, setMultiModalOpen] = useState(false);
  const [multiForm, setMultiForm] = useState({
    metode: 'Tunai' as 'Tunai' | 'Transfer',
    tanggal_bayar: new Date().toISOString().slice(0, 10),
    catatan: '',
  });
  const [struk, setStruk] = useState<null | {
    siswaNama: string;
    nis: string;
    kelas: string;
    tanggal: string;
    metode: string;
    catatan: string;
    rows: { nama: string; bulan: string; jumlah: number }[];
    total: number;
    nomor: string[];
  }>(null);
  const [strukModalOpen, setStrukModalOpen] = useState(false);
  const [printStruk, setPrintStruk] = useState(false);

  const handleCetakStruk = () => {
    setPrintStruk(true);
    const reset = () => {
      setPrintStruk(false);
      window.removeEventListener('afterprint', reset);
    };
    window.addEventListener('afterprint', reset);
    setTimeout(() => window.print(), 150);
  };

  // Ganti siswa -> kosongkan centang
  useEffect(() => {
    setPilihan({});
  }, [selectedSiswaId]);

  // Kartu SPP / I'anah Syahriyah
  const [kartuPos, setKartuPos] = useState('');
  const [printKartu, setPrintKartu] = useState(false);

  const handleCetakKartu = () => {
    setPrintKartu(true);
    const reset = () => {
      setPrintKartu(false);
      window.removeEventListener('afterprint', reset);
    };
    window.addEventListener('afterprint', reset);
    setTimeout(() => window.print(), 150);
  };

  // Handle URL search params on mount
  useEffect(() => {
    const sId = searchParams.get('siswaId');
    if (sId) {
      setSelectedSiswaId(Number(sId));
    }
  }, [searchParams]);

  // Queries
  const { data: taList = [] } = useQuery({
    queryKey: ['tahun-ajaran'],
    queryFn: async () => {
      const res = await api.get<TahunAjaran[]>('/api/tahun-ajaran');
      return res.data || [];
    },
  });

  const activeTa = taList.find((t) => t.is_active) || taList[0];

  // Profil madrasah untuk kop Kartu SPP
  const { data: profilMadrasah } = useQuery({
    queryKey: ['pengaturan'],
    queryFn: async () => {
      const res = await api.get<any>('/api/pengaturan');
      return res.data;
    },
    retry: false,
  });

  // Query search siswa
  const { data: searchResults = [] } = useQuery({
    queryKey: ['search-siswa-keuangan', searchQuery],
    queryFn: async () => {
      if (!searchQuery || searchQuery.trim().length < 2) return [];
      const res = await api.get<any>(`/api/siswa?search=${encodeURIComponent(searchQuery)}&limit=10`);
      return res.data?.data || [];
    },
    enabled: Boolean(searchQuery && searchQuery.trim().length >= 2),
  });

  // Query Data Siswa Terpilih
  const { data: selectedSiswa } = useQuery({
    queryKey: ['siswa-detail', selectedSiswaId],
    queryFn: async () => {
      if (!selectedSiswaId) return null;
      const res = await api.get<Siswa>(`/api/siswa/${selectedSiswaId}`);
      return res.data || null;
    },
    enabled: Boolean(selectedSiswaId),
  });

  // Query Tagihan Siswa Terpilih
  const { data: tagihanList = [], isLoading: isLoadingTagihan } = useQuery({
    queryKey: ['tagihan-siswa-aktif', selectedSiswaId, activeTa?.id],
    queryFn: async () => {
      if (!selectedSiswaId) return [];
      const res = await api.get<TagihanSiswa[]>(
        `/api/keuangan/tagihan/siswa/${selectedSiswaId}?tahun_ajaran_id=${activeTa?.id || ''}`
      );
      return res.data || [];
    },
    enabled: Boolean(selectedSiswaId),
  });

  // Query Riwayat Pembayaran Siswa Terpilih
  const { data: riwayatTransaksi = [], isLoading: isLoadingRiwayat } = useQuery({
    queryKey: ['riwayat-pembayaran-siswa', selectedSiswaId],
    queryFn: async () => {
      if (!selectedSiswaId) return [];
      const res = await api.get<any>(`/api/keuangan/pembayaran?siswa_id=${selectedSiswaId}&limit=200`);
      return res.data || [];
    },
    enabled: Boolean(selectedSiswaId && selectedSiswa),
  });

  // Mutations
  const bayarMutation = useMutation({
    mutationFn: (data: any) => api.post('/api/keuangan/pembayaran', data),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['tagihan-siswa-aktif'] });
      queryClient.invalidateQueries({ queryKey: ['riwayat-pembayaran-siswa'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-keuangan'] });
      queryClient.invalidateQueries({ queryKey: ['tagihan-list'] });
      queryClient.invalidateQueries({ queryKey: ['tunggakan'] });
      success(res.message || 'Pembayaran berhasil dicatat.');
      setBayarModalOpen(false);
      if (res.data?.transaksi?.id) {
        setSelectedTxId(res.data.transaksi.id); // Tampilkan langsung kwitansi
      }
    },
    onError: (err: any) => error(err.message || 'Gagal memproses pembayaran.'),
  });

  const voidMutation = useMutation({
    mutationFn: ({ id, alasan }: { id: number; alasan: string }) =>
      api.post(`/api/keuangan/pembayaran/${id}/batal`, { alasan_batal: alasan }),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['tagihan-siswa-aktif'] });
      queryClient.invalidateQueries({ queryKey: ['riwayat-pembayaran-siswa'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-keuangan'] });
      queryClient.invalidateQueries({ queryKey: ['tagihan-list'] });
      queryClient.invalidateQueries({ queryKey: ['tunggakan'] });
      warning(res.message || 'Pembayaran berhasil dibatalkan (void).');
      setVoidModalOpen(false);
      setVoidTx(null);
      setAlasanBatal('');
    },
    onError: (err: any) => error(err.message || 'Gagal membatalkan transaksi.'),
  });

  const rp = (n: number) => `Rp ${Number(n).toLocaleString('id-ID')}`;

  const multiMutation = useMutation({
    mutationFn: ({ payload }: { payload: any; snapshot: any }) =>
      api.post('/api/keuangan/pembayaran/multi', payload),
    onSuccess: (res: any, vars) => {
      queryClient.invalidateQueries({ queryKey: ['tagihan-siswa-aktif'] });
      queryClient.invalidateQueries({ queryKey: ['riwayat-pembayaran-siswa'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-keuangan'] });
      queryClient.invalidateQueries({ queryKey: ['tagihan-list'] });
      queryClient.invalidateQueries({ queryKey: ['tunggakan'] });
      success(res.message || 'Pembayaran gabungan berhasil dicatat.');
      setStruk({
        ...vars.snapshot,
        nomor: res.data?.nomor_transaksi || [],
        total: Number(res.data?.total ?? vars.snapshot.total),
      });
      setMultiModalOpen(false);
      setPilihan({});
      setStrukModalOpen(true);
    },
    onError: (err: any) => error(err.message || 'Gagal memproses pembayaran gabungan.'),
  });

  const togglePilih = (t: TagihanSiswa) => {
    setPilihan((prev) => {
      const next = { ...prev };
      if (next[t.id] !== undefined) delete next[t.id];
      else next[t.id] = Number(t.sisa);
      return next;
    });
  };

  const belumLunas = tagihanList.filter((t) => t.status !== 'lunas');
  const itemPilihan = tagihanList.filter((t) => pilihan[t.id] !== undefined);
  const totalPilihan = itemPilihan.reduce((a, t) => a + Number(pilihan[t.id] || 0), 0);

  const togglePilihSemua = () => {
    if (itemPilihan.length === belumLunas.length) {
      setPilihan({});
    } else {
      const all: Record<number, number> = {};
      belumLunas.forEach((t) => (all[t.id] = Number(t.sisa)));
      setPilihan(all);
    }
  };

  const handleOpenMulti = () => {
    setMultiForm({
      metode: 'Tunai',
      tanggal_bayar: new Date().toISOString().slice(0, 10),
      catatan: '',
    });
    setMultiModalOpen(true);
  };

  const handleMultiSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSiswaId || !selectedSiswa || itemPilihan.length === 0) return;

    for (const t of itemPilihan) {
      const nominal = Number(pilihan[t.id]);
      if (!nominal || nominal <= 0) {
        error(`Nominal untuk ${t.jenisPembayaran?.nama || 'tagihan'} harus lebih dari 0.`);
        return;
      }
      if (nominal > Number(t.sisa)) {
        error(`Nominal ${t.jenisPembayaran?.nama || 'tagihan'} melebihi sisa tagihan (${rp(Number(t.sisa))}).`);
        return;
      }
    }

    multiMutation.mutate({
      payload: {
        siswa_id: selectedSiswaId,
        items: itemPilihan.map((t) => ({
          tagihan_id: t.id,
          jumlah_bayar: Number(pilihan[t.id]),
        })),
        metode: multiForm.metode,
        tanggal_bayar: multiForm.tanggal_bayar,
        catatan: multiForm.catatan,
      },
      snapshot: {
        siswaNama: selectedSiswa.nama,
        nis: String(selectedSiswa.nis || ''),
        kelas: selectedSiswa.kelas?.nama || '-',
        tanggal: multiForm.tanggal_bayar,
        metode: multiForm.metode,
        catatan: multiForm.catatan,
        rows: itemPilihan.map((t) => ({
          nama: t.jenisPembayaran?.nama || 'Pos Pembayaran',
          bulan: t.bulan || '',
          jumlah: Number(pilihan[t.id]),
        })),
        total: totalPilihan,
      },
    });
  };

  const handleOpenBayar = (tagihan: TagihanSiswa) => {
    setSelectedTagihan(tagihan);
    setBayarForm({
      jumlah_bayar: Number(tagihan.sisa), // Default lunas, tapi user bisa ubah untuk cicilan
      metode: 'Tunai',
      tanggal_bayar: new Date().toISOString().slice(0, 10),
      catatan: '',
    });
    setBayarModalOpen(true);
  };

  const handleBayarSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTagihan || !selectedSiswaId) return;

    if (bayarForm.jumlah_bayar <= 0) {
      error('Jumlah pembayaran harus lebih dari 0.');
      return;
    }

    if (bayarForm.jumlah_bayar > Number(selectedTagihan.sisa)) {
      error(`Jumlah pembayaran tidak boleh melebihi sisa tagihan (Rp ${selectedTagihan.sisa.toLocaleString('id-ID')}).`);
      return;
    }

    bayarMutation.mutate({
      tagihan_id: selectedTagihan.id,
      siswa_id: selectedSiswaId,
      jumlah_bayar: bayarForm.jumlah_bayar,
      metode: bayarForm.metode,
      tanggal_bayar: bayarForm.tanggal_bayar,
      catatan: bayarForm.catatan,
    });
  };

  const handleOpenVoid = (tx: TransaksiPembayaran) => {
    setVoidTx(tx);
    setAlasanBatal('');
    setVoidModalOpen(true);
  };

  const handleVoidSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!voidTx) return;
    if (!alasanBatal.trim()) {
      error('Alasan pembatalan wajib diisi.');
      return;
    }
    voidMutation.mutate({ id: voidTx.id, alasan: alasanBatal.trim() });
  };

  // Kalkulasi Ringkasan Tagihan Siswa
  const totalTagihanSiswa = tagihanList.reduce((acc, t) => acc + Number(t.nominal), 0);
  const totalTerbayarSiswa = tagihanList.reduce((acc, t) => acc + Number(t.terbayar), 0);
  const totalSisaSiswa = tagihanList.reduce((acc, t) => acc + Number(t.sisa), 0);

  // Data Kartu SPP: tagihan bulanan dipetakan ke 12 bulan tahun ajaran
  const tagihanBulanan = tagihanList.filter((t) => t.bulan);
  const namaPosBulanan = Array.from(
    new Set(tagihanBulanan.map((t) => t.jenisPembayaran?.nama || 'Pos Bulanan'))
  );
  const namaKartu = namaPosBulanan.includes(kartuPos)
    ? kartuPos
    : namaPosBulanan.find((n) => /spp|syahriyah|ianah|i'anah/i.test(n)) || namaPosBulanan[0] || '';

  const barisKartu = URUTAN_BULAN_AJARAN.map((bulan) => {
    const t = tagihanBulanan.find(
      (x) => (x.jenisPembayaran?.nama || 'Pos Bulanan') === namaKartu && cocokBulan(x.bulan, bulan)
    );
    const trxValid = t
      ? (riwayatTransaksi as any[]).filter(
          (tx) => tx.status === 'valid' && (tx.tagihan_id === t.id || tx.tagihan?.id === t.id)
        )
      : [];
    const trxTerakhir = [...trxValid]
      .sort((a, b) => String(a.tanggal_bayar).localeCompare(String(b.tanggal_bayar)))
      .pop();
    return { bulan, t, trx: trxTerakhir };
  });
  const kartuTotalNominal = barisKartu.reduce((a, r) => a + Number(r.t?.nominal || 0), 0);
  const kartuTotalTerbayar = barisKartu.reduce((a, r) => a + Number(r.t?.terbayar || 0), 0);
  const kartuTotalSisa = barisKartu.reduce((a, r) => a + Number(r.t?.sisa || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-800">
          Kasir & Pencatatan Pembayaran Siswa
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          Cari siswa untuk melihat status tagihan, mencatat pembayaran tunai/transfer, mendukung sistem cicilan, dan menerbitkan kwitansi resmi.
        </p>
      </div>

      {/* Pencarian Siswa */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs space-y-4">
        <label className="block text-xs font-bold text-slate-700">
          Pencarian Siswa (Ketik Nama atau NIS):
        </label>
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
          <input
            type="text"
            placeholder="Ketik minimal 2 huruf, misal: Fatih, Aisyah, atau 242507001..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-xs border border-slate-200 rounded-xl focus:outline-emerald-600 bg-slate-50/50"
          />
        </div>

        {/* Dropdown Auto-Suggest Results */}
        {searchResults.length > 0 && (
          <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100 bg-white shadow-lg">
            {searchResults.map((s: Siswa) => (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  setSelectedSiswaId(s.id);
                  setSearchQuery('');
                }}
                className="w-full px-4 py-2.5 text-left flex items-center justify-between hover:bg-emerald-50/60 transition-colors cursor-pointer"
              >
                <div>
                  <span className="font-bold text-xs text-slate-800">{s.nama}</span>
                  <span className="text-[11px] text-slate-500 ml-2 font-mono">
                    NIS: {s.nis} • Kelas: {s.kelas?.nama || '-'}
                  </span>
                </div>
                <span className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1">
                  Pilih <ArrowRight className="w-3 h-3" />
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Panel Siswa Terpilih & Tagihan */}
      {selectedSiswa ? (
        <div className="space-y-6">
          {/* Header Profil Siswa & Ringkasan Saldo */}
          <div className="bg-gradient-to-r from-emerald-800 to-teal-900 text-white p-6 rounded-2xl shadow-sm flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center border border-white/20 text-white shrink-0">
                <User className="w-7 h-7" />
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold tracking-wider text-emerald-200">
                  Data Siswa Terpilih
                </span>
                <h3 className="text-lg font-bold">{selectedSiswa.nama}</h3>
                <p className="text-xs text-emerald-100 mt-0.5 font-mono">
                  NIS: {selectedSiswa.nis} • NISN: {selectedSiswa.nisn || '-'} • Kelas:{' '}
                  {selectedSiswa.kelas?.nama || '-'}
                </p>
              </div>
            </div>

            {/* Statistik Saldo */}
            <div className="grid grid-cols-3 gap-4 border-t md:border-t-0 md:border-l border-white/20 pt-4 md:pt-0 md:pl-6 text-center">
              <div>
                <span className="text-[10px] text-emerald-200 block uppercase font-medium">
                  Total Tagihan
                </span>
                <span className="text-sm font-extrabold font-mono">
                  Rp {totalTagihanSiswa.toLocaleString('id-ID')}
                </span>
              </div>
              <div>
                <span className="text-[10px] text-emerald-200 block uppercase font-medium">
                  Telah Dibayar
                </span>
                <span className="text-sm font-extrabold font-mono text-emerald-300">
                  Rp {totalTerbayarSiswa.toLocaleString('id-ID')}
                </span>
              </div>
              <div>
                <span className="text-[10px] text-rose-300 block uppercase font-bold">
                  Sisa Tunggakan
                </span>
                <span className="text-sm font-extrabold font-mono text-rose-300">
                  Rp {totalSisaSiswa.toLocaleString('id-ID')}
                </span>
              </div>
            </div>
          </div>

          {/* Tabel Tagihan Siswa */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden space-y-3 p-5">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-emerald-600" />
                Daftar Tagihan Siswa (Tahun Ajaran {activeTa?.tahun || '-'})
              </h4>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 font-medium">
                  {tagihanList.length} Pos Tagihan
                </span>
                {namaPosBulanan.length > 1 && (
                  <select
                    value={namaKartu}
                    onChange={(e) => setKartuPos(e.target.value)}
                    className="px-2 py-1 text-[11px] border border-slate-200 rounded-lg bg-white focus:outline-emerald-600"
                    title="Pilih pos untuk kartu"
                  >
                    {namaPosBulanan.map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                )}
                <button
                  type="button"
                  onClick={handleCetakKartu}
                  disabled={!namaKartu}
                  title={namaKartu ? 'Cetak kartu kontrol pembayaran bulanan' : 'Belum ada tagihan bulanan'}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed text-emerald-700 rounded-lg text-[11px] font-semibold transition-colors cursor-pointer"
                >
                  <Printer className="w-3.5 h-3.5" />
                  Cetak Kartu SPP
                </button>
              </div>
            </div>

            {canEdit && itemPilihan.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                <div className="text-xs text-emerald-900">
                  <span className="font-bold">{itemPilihan.length} pos dipilih</span>
                  <span className="mx-2">•</span>
                  Total: <span className="font-mono font-extrabold">{rp(totalPilihan)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPilihan({})}
                    className="px-3 py-1.5 text-[11px] font-semibold text-slate-600 hover:bg-white rounded-lg cursor-pointer"
                  >
                    Kosongkan
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenMulti}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-[11px] font-semibold cursor-pointer"
                  >
                    <ListChecks className="w-3.5 h-3.5" />
                    Bayar Terpilih & Cetak Satu Kwitansi
                  </button>
                </div>
              </div>
            )}

            {isLoadingTagihan ? (
              <TableSkeleton rows={4} cols={6} />
            ) : tagihanList.length === 0 ? (
              <div className="py-8 text-center text-slate-400 text-xs">
                Siswa ini belum memiliki tagihan pada tahun ajaran ini.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-500 font-semibold bg-slate-50/70">
                      {canEdit && (
                        <th className="py-2.5 px-3 w-8">
                          <input
                            type="checkbox"
                            title="Pilih semua yang belum lunas"
                            checked={belumLunas.length > 0 && itemPilihan.length === belumLunas.length}
                            onChange={togglePilihSemua}
                            disabled={belumLunas.length === 0}
                            className="cursor-pointer accent-emerald-700"
                          />
                        </th>
                      )}
                      <th className="py-2.5 px-3">Pos Pembayaran</th>
                      <th className="py-2.5 px-3">Bulan</th>
                      <th className="py-2.5 px-3 font-mono">Nominal</th>
                      <th className="py-2.5 px-3 font-mono">Terbayar</th>
                      <th className="py-2.5 px-3 font-mono">Sisa Tagihan</th>
                      <th className="py-2.5 px-3 text-center">Status</th>
                      {canEdit && <th className="py-2.5 px-3 text-right">Aksi</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {tagihanList.map((t) => (
                      <tr key={t.id} className="hover:bg-slate-50/70 transition-colors">
                        {canEdit && (
                          <td className="py-2.5 px-3">
                            {t.status !== 'lunas' ? (
                              <input
                                type="checkbox"
                                checked={pilihan[t.id] !== undefined}
                                onChange={() => togglePilih(t)}
                                className="cursor-pointer accent-emerald-700"
                              />
                            ) : null}
                          </td>
                        )}
                        <td className="py-2.5 px-3 font-bold text-slate-800">
                          {t.jenisPembayaran?.nama || 'Pos Pembayaran'}
                        </td>
                        <td className="py-2.5 px-3 font-medium text-slate-700">
                          {t.bulan || '-'}
                        </td>
                        <td className="py-2.5 px-3 font-mono text-slate-700">
                          Rp {Number(t.nominal).toLocaleString('id-ID')}
                        </td>
                        <td className="py-2.5 px-3 font-mono font-semibold text-emerald-700">
                          Rp {Number(t.terbayar).toLocaleString('id-ID')}
                        </td>
                        <td className="py-2.5 px-3 font-mono font-bold text-rose-600">
                          Rp {Number(t.sisa).toLocaleString('id-ID')}
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          {t.status === 'lunas' ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full">
                              <CheckCircle2 className="w-3 h-3" /> Lunas
                            </span>
                          ) : t.status === 'sebagian' ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full">
                              <Clock className="w-3 h-3" /> Dicicil
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-800 bg-rose-100 px-2 py-0.5 rounded-full">
                              <AlertCircle className="w-3 h-3" /> Belum Bayar
                            </span>
                          )}
                        </td>
                        {canEdit && (
                          <td className="py-2.5 px-3 text-right">
                            {t.status !== 'lunas' ? (
                              <button
                                onClick={() => handleOpenBayar(t)}
                                className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-lg text-xs transition-colors cursor-pointer shadow-2xs"
                              >
                                <Wallet className="w-3.5 h-3.5" />
                                Bayar / Cicil
                              </button>
                            ) : (
                              <span className="text-[11px] text-emerald-600 font-semibold inline-flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> Tuntas
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Tabel Riwayat Pembayaran Siswa */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden space-y-3 p-5">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <div>
                <h4 className="text-sm font-bold text-slate-800">
                  Riwayat Pembayaran & Kwitansi Siswa Ini
                </h4>
                <p className="text-[11px] text-slate-400">
                  Semua transaksi sah dan bukti pembayaran yang pernah dicatat
                </p>
              </div>
            </div>

            {isLoadingRiwayat ? (
              <TableSkeleton rows={3} cols={6} />
            ) : riwayatTransaksi.length === 0 ? (
              <div className="py-8 text-center text-slate-400 text-xs">
                Belum ada riwayat pembayaran yang tercatat untuk siswa ini.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-500 font-semibold bg-slate-50/70">
                      <th className="py-2.5 px-3">No. Kwitansi</th>
                      <th className="py-2.5 px-3">Tanggal Bayar</th>
                      <th className="py-2.5 px-3">Pos Tagihan</th>
                      <th className="py-2.5 px-3 font-mono">Jumlah Bayar</th>
                      <th className="py-2.5 px-3">Metode</th>
                      <th className="py-2.5 px-3">Status</th>
                      <th className="py-2.5 px-3 text-right">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {riwayatTransaksi.map((tx: TransaksiPembayaran) => (
                      <tr key={tx.id} className="hover:bg-slate-50/70 transition-colors">
                        <td className="py-2.5 px-3 font-mono font-bold text-slate-800">
                          {tx.nomor_transaksi}
                        </td>
                        <td className="py-2.5 px-3 text-slate-600 whitespace-nowrap">
                          {tx.tanggal_bayar}
                        </td>
                        <td className="py-2.5 px-3 text-slate-800 font-medium">
                          {tx.tagihan?.jenisPembayaran?.nama || 'Pos Tagihan'}{' '}
                          {tx.tagihan?.bulan && `(${tx.tagihan.bulan})`}
                        </td>
                        <td className="py-2.5 px-3 font-mono font-bold text-emerald-700">
                          Rp {Number(tx.jumlah_bayar).toLocaleString('id-ID')}
                        </td>
                        <td className="py-2.5 px-3">
                          <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700">
                            {tx.metode}
                          </span>
                        </td>
                        <td className="py-2.5 px-3">
                          {tx.status === 'valid' ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
                              <CheckCircle2 className="w-3 h-3" /> Sah / Valid
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-semibold text-rose-700 bg-rose-50 px-2 py-0.5 rounded cursor-help"
                              title={`Dibatalkan: ${tx.alasan_batal || '-'}`}
                            >
                              <Ban className="w-3 h-3" /> Dibatalkan
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => setSelectedTxId(tx.id)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-[11px] font-semibold transition-colors cursor-pointer"
                              title="Cetak Kwitansi"
                            >
                              <Printer className="w-3.5 h-3.5" />
                              Kwitansi
                            </button>

                            {canEdit && tx.status === 'valid' && (
                              <button
                                onClick={() => handleOpenVoid(tx)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg text-[11px] font-semibold transition-colors cursor-pointer"
                                title="Batalkan Pembayaran (Void)"
                              >
                                <Ban className="w-3.5 h-3.5" />
                                Batal
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white p-12 rounded-2xl border border-slate-200/80 shadow-xs text-center space-y-3">
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center mx-auto">
            <Search className="w-6 h-6" />
          </div>
          <h3 className="text-sm font-bold text-slate-800">
            Pilih Siswa untuk Memulai Pembayaran
          </h3>
          <p className="text-xs text-slate-500 max-w-sm mx-auto">
            Gunakan kolom pencarian di atas dengan mengetikkan nama siswa atau NIS untuk melihat daftar tagihan dan mencatat pembayaran.
          </p>
        </div>
      )}

      {/* Modal Form Bayar Tagihan (Mendukung Cicilan) */}
      <Modal
        isOpen={bayarModalOpen}
        onClose={() => setBayarModalOpen(false)}
        title="Formulir Pembayaran Kasir"
        size="md"
      >
        <form onSubmit={handleBayarSubmit} className="space-y-4">
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-slate-500">Nama Siswa:</span>
              <span className="font-bold text-slate-800">{selectedSiswa?.nama}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Tagihan:</span>
              <span className="font-semibold text-slate-800">
                {selectedTagihan?.jenisPembayaran?.nama}{' '}
                {selectedTagihan?.bulan && `(${selectedTagihan.bulan})`}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Total Tagihan:</span>
              <span className="font-mono text-slate-800">
                Rp {Number(selectedTagihan?.nominal || 0).toLocaleString('id-ID')}
              </span>
            </div>
            <div className="flex justify-between pt-1 border-t border-slate-200">
              <span className="text-rose-600 font-bold">Sisa yang Harus Dibayar:</span>
              <span className="font-mono font-bold text-rose-600">
                Rp {Number(selectedTagihan?.sisa || 0).toLocaleString('id-ID')}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Jumlah Bayar (Rupiah) <span className="text-rose-500">*</span>
            </label>
            <input
              type="number"
              min="1"
              max={Number(selectedTagihan?.sisa || 0)}
              required
              value={bayarForm.jumlah_bayar}
              onChange={(e) =>
                setBayarForm({ ...bayarForm, jumlah_bayar: Number(e.target.value) })
              }
              className="w-full px-3 py-2 text-sm font-mono font-bold border border-slate-200 rounded-xl focus:outline-emerald-600"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              * Masukkan nominal pelunasan penuh atau nominal cicilan sesuai uang yang diserahkan.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Metode Pembayaran
              </label>
              <select
                value={bayarForm.metode}
                onChange={(e) =>
                  setBayarForm({
                    ...bayarForm,
                    metode: e.target.value as 'Tunai' | 'Transfer',
                  })
                }
                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-emerald-600 bg-white"
              >
                <option value="Tunai">Tunai / Cash</option>
                <option value="Transfer">Transfer Bank</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Tanggal Pembayaran
              </label>
              <input
                type="date"
                required
                value={bayarForm.tanggal_bayar}
                onChange={(e) =>
                  setBayarForm({ ...bayarForm, tanggal_bayar: e.target.value })
                }
                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-emerald-600"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Catatan Transaksi (Opsional)
            </label>
            <input
              type="text"
              placeholder="Contoh: No. Ref Transfer, titipan wali santri, dll"
              value={bayarForm.catatan}
              onChange={(e) => setBayarForm({ ...bayarForm, catatan: e.target.value })}
              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-emerald-600"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={() => setBayarModalOpen(false)}
              className="px-4 py-2 border border-slate-200 text-slate-600 rounded-xl text-xs font-semibold hover:bg-slate-50 transition-colors cursor-pointer"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={bayarMutation.isPending}
              className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-300 text-white rounded-xl text-xs font-semibold shadow-xs transition-colors cursor-pointer"
            >
              {bayarMutation.isPending ? 'Menyimpan...' : 'Simpan & Cetak Kwitansi'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal Pembayaran Gabungan */}
      <Modal
        isOpen={multiModalOpen}
        onClose={() => setMultiModalOpen(false)}
        title="Pembayaran Gabungan (Satu Kwitansi)"
        size="md"
      >
        <form onSubmit={handleMultiSubmit} className="space-y-4">
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs">
            <div className="flex justify-between mb-2">
              <span className="text-slate-500">Nama Siswa:</span>
              <span className="font-bold text-slate-800">{selectedSiswa?.nama}</span>
            </div>
            <div className="space-y-2 border-t border-slate-200 pt-2">
              {itemPilihan.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 truncate">
                      {t.jenisPembayaran?.nama || 'Pos Pembayaran'} {t.bulan ? `(${t.bulan})` : ''}
                    </p>
                    <p className="text-[10px] text-slate-400">Sisa: {rp(Number(t.sisa))}</p>
                  </div>
                  <input
                    type="number"
                    min="1"
                    max={Number(t.sisa)}
                    required
                    value={pilihan[t.id] ?? 0}
                    onChange={(e) =>
                      setPilihan((prev) => ({ ...prev, [t.id]: Number(e.target.value) }))
                    }
                    className="w-32 px-2 py-1.5 text-xs font-mono font-bold text-right border border-slate-200 rounded-lg focus:outline-emerald-600"
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between pt-2 mt-2 border-t border-slate-200">
              <span className="font-bold text-slate-700">TOTAL</span>
              <span className="font-mono font-extrabold text-emerald-700">{rp(totalPilihan)}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Metode Pembayaran</label>
              <select
                value={multiForm.metode}
                onChange={(e) =>
                  setMultiForm({ ...multiForm, metode: e.target.value as 'Tunai' | 'Transfer' })
                }
                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-emerald-600 bg-white"
              >
                <option value="Tunai">Tunai / Cash</option>
                <option value="Transfer">Transfer Bank</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Tanggal Pembayaran</label>
              <input
                type="date"
                required
                value={multiForm.tanggal_bayar}
                onChange={(e) => setMultiForm({ ...multiForm, tanggal_bayar: e.target.value })}
                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-emerald-600"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Catatan (Opsional)</label>
            <input
              type="text"
              value={multiForm.catatan}
              onChange={(e) => setMultiForm({ ...multiForm, catatan: e.target.value })}
              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-emerald-600"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={() => setMultiModalOpen(false)}
              className="px-4 py-2 border border-slate-200 text-slate-600 rounded-xl text-xs font-semibold hover:bg-slate-50 cursor-pointer"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={multiMutation.isPending || itemPilihan.length === 0}
              className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-300 text-white rounded-xl text-xs font-semibold cursor-pointer"
            >
              {multiMutation.isPending ? 'Menyimpan...' : 'Simpan Pembayaran'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal Hasil: Pembayaran Gabungan Berhasil */}
      <Modal
        isOpen={strukModalOpen}
        onClose={() => setStrukModalOpen(false)}
        title="Pembayaran Berhasil Dicatat"
        size="md"
      >
        {struk && (
          <div className="space-y-4 text-xs">
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl space-y-1">
              <p className="font-bold text-emerald-900">{struk.siswaNama}</p>
              {struk.rows.map((r, i) => (
                <div key={i} className="flex justify-between">
                  <span>{r.nama} {r.bulan && `(${r.bulan})`}</span>
                  <span className="font-mono">{rp(r.jumlah)}</span>
                </div>
              ))}
              <div className="flex justify-between pt-1 mt-1 border-t border-emerald-200 font-extrabold">
                <span>TOTAL</span>
                <span className="font-mono">{rp(struk.total)}</span>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 font-mono">
              No. Transaksi: {struk.nomor.join(', ') || '-'}
            </p>
            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setStrukModalOpen(false)}
                className="px-4 py-2 border border-slate-200 text-slate-600 rounded-xl text-xs font-semibold hover:bg-slate-50 cursor-pointer"
              >
                Tutup
              </button>
              <button
                type="button"
                onClick={handleCetakStruk}
                className="inline-flex items-center gap-1 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-semibold cursor-pointer"
              >
                <Printer className="w-3.5 h-3.5" />
                Cetak Kwitansi
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal Batalkan Pembayaran (VOID dengan Alasan Wajib) */}
      <Modal
        isOpen={voidModalOpen}
        onClose={() => setVoidModalOpen(false)}
        title="Batalkan Pembayaran (Void)"
        size="md"
      >
        <form onSubmit={handleVoidSubmit} className="space-y-4">
          <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-800 flex items-start gap-2">
            <ShieldAlert className="w-4 h-4 shrink-0 text-rose-600 mt-0.5" />
            <div>
              <p className="font-bold">Perhatian: Transaksi Tidak Dapat Dihapus!</p>
              <p className="mt-0.5 text-[11px] text-rose-700">
                Sesuai standar audit keuangan madrasah, transaksi yang sudah terjadi hanya dapat dibatalkan (void) dengan menyertakan alasan yang sah. Nominal tagihan akan dikembalikan ke saldo siswa secara otomatis.
              </p>
            </div>
          </div>

          <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1 text-xs font-mono">
            <div>No. Kwitansi: <span className="font-bold text-slate-900">{voidTx?.nomor_transaksi}</span></div>
            <div>Jumlah: <span className="font-bold text-emerald-700">Rp {Number(voidTx?.jumlah_bayar || 0).toLocaleString('id-ID')}</span></div>
            <div>Tanggal: <span className="text-slate-700">{voidTx?.tanggal_bayar}</span></div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Alasan Pembatalan <span className="text-rose-500">*</span>
            </label>
            <textarea
              required
              rows={3}
              placeholder="Contoh: Kesalahan input nominal oleh kasir, cek/transfer ditolak bank, salah pilih siswa, dll..."
              value={alasanBatal}
              onChange={(e) => setAlasanBatal(e.target.value)}
              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-rose-500"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={() => setVoidModalOpen(false)}
              className="px-4 py-2 border border-slate-200 text-slate-600 rounded-xl text-xs font-semibold hover:bg-slate-50 transition-colors cursor-pointer"
            >
              Kembali
            </button>
            <button
              type="submit"
              disabled={voidMutation.isPending}
              className="px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:bg-slate-300 text-white rounded-xl text-xs font-semibold shadow-xs transition-colors cursor-pointer"
            >
              {voidMutation.isPending ? 'Membatalkan...' : 'Konfirmasi Batalkan Transaksi'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Lembar Cetak Kartu SPP / I'anah Syahriyah (hanya dirender saat mencetak kartu) */}
      {printKartu && selectedSiswa && (
        <>
          <style>{`
            @media print {
              @page { size: auto; margin: 10mm; }
              html, body, #root { height: auto !important; overflow: visible !important; background: #fff !important; }
              body * { visibility: hidden !important; }
              .kartu-spp-sheet, .kartu-spp-sheet * { visibility: visible !important; }
              .kartu-spp-sheet { position: absolute !important; left: 0 !important; top: 0 !important; width: 100% !important; }
              .kartu-spp-sheet tr { break-inside: avoid; page-break-inside: avoid; }
            }
          `}</style>
          <div className="kartu-spp-sheet hidden print:block text-slate-900">
            <div className="border-b-2 border-slate-800 pb-2 mb-3">
              <div className="flex items-center gap-3">
                {profilMadrasah?.logo_url && (
                  <img src={profilMadrasah.logo_url} alt="Logo" className="w-12 h-12 object-contain shrink-0" />
                )}
                <div className="flex-1 text-center">
                  <p className="text-[10px] font-bold tracking-widest uppercase">
                    KEMENTERIAN AGAMA REPUBLIK INDONESIA
                  </p>
                  <h2 className="text-sm font-extrabold uppercase tracking-tight">
                    {profilMadrasah?.nama || 'Madrasah'}
                  </h2>
                  <p className="text-[10px]">
                    NSM: {profilMadrasah?.nsm || '-'} • NPSN: {profilMadrasah?.npsn || '-'}
                  </p>
                  <p className="text-[9px]">
                    {profilMadrasah?.alamat || ''}
                    {profilMadrasah?.telepon ? ` • Telp: ${profilMadrasah.telepon}` : ''}
                  </p>
                </div>
              </div>
              <h1 className="text-base font-extrabold uppercase tracking-wider text-center mt-2">
                KARTU KONTROL PEMBAYARAN {namaKartu}
              </h1>
              <p className="text-xs text-center">Tahun Ajaran {activeTa?.tahun || '-'}</p>
            </div>

            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs mb-3">
              <div>Nama Siswa: <strong>{selectedSiswa.nama}</strong></div>
              <div>Kelas: <strong>{selectedSiswa.kelas?.nama || '-'}</strong></div>
              <div>NIS / NISN: <strong>{selectedSiswa.nis} / {selectedSiswa.nisn || '-'}</strong></div>
              <div>Dicetak: <strong>{new Date().toLocaleDateString('id-ID', { dateStyle: 'long' })}</strong></div>
            </div>

            <table className="w-full text-xs border border-slate-500 border-collapse">
              <thead>
                <tr className="bg-slate-100 font-bold text-center">
                  <th className="border border-slate-500 py-1 px-2 w-8">No</th>
                  <th className="border border-slate-500 py-1 px-2 text-left">Bulan</th>
                  <th className="border border-slate-500 py-1 px-2 text-right">Nominal</th>
                  <th className="border border-slate-500 py-1 px-2 text-right">Terbayar</th>
                  <th className="border border-slate-500 py-1 px-2 text-right">Sisa</th>
                  <th className="border border-slate-500 py-1 px-2">Status</th>
                  <th className="border border-slate-500 py-1 px-2">Tgl Bayar</th>
                  <th className="border border-slate-500 py-1 px-2">No. Kwitansi</th>
                  <th className="border border-slate-500 py-1 px-2 w-16">Paraf</th>
                </tr>
              </thead>
              <tbody>
                {barisKartu.map((r, i) => (
                  <tr key={r.bulan} className="h-7">
                    <td className="border border-slate-500 px-2 text-center">{i + 1}</td>
                    <td className="border border-slate-500 px-2 font-semibold">{r.bulan}</td>
                    <td className="border border-slate-500 px-2 text-right font-mono">
                      {r.t ? Number(r.t.nominal).toLocaleString('id-ID') : '-'}
                    </td>
                    <td className="border border-slate-500 px-2 text-right font-mono">
                      {r.t ? Number(r.t.terbayar).toLocaleString('id-ID') : '-'}
                    </td>
                    <td className="border border-slate-500 px-2 text-right font-mono">
                      {r.t ? Number(r.t.sisa).toLocaleString('id-ID') : '-'}
                    </td>
                    <td className="border border-slate-500 px-2 text-center font-bold">
                      {!r.t ? '-' : r.t.status === 'lunas' ? 'LUNAS' : r.t.status === 'sebagian' ? 'KURANG' : 'BELUM'}
                    </td>
                    <td className="border border-slate-500 px-2 text-center">{r.trx?.tanggal_bayar || ''}</td>
                    <td className="border border-slate-500 px-2 text-center font-mono text-[10px]">
                      {r.trx?.nomor_transaksi || ''}
                    </td>
                    <td className="border border-slate-500 px-2" />
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 font-bold">
                  <td colSpan={2} className="border border-slate-500 px-2 py-1 text-right">TOTAL</td>
                  <td className="border border-slate-500 px-2 text-right font-mono">{kartuTotalNominal.toLocaleString('id-ID')}</td>
                  <td className="border border-slate-500 px-2 text-right font-mono">{kartuTotalTerbayar.toLocaleString('id-ID')}</td>
                  <td className="border border-slate-500 px-2 text-right font-mono">{kartuTotalSisa.toLocaleString('id-ID')}</td>
                  <td colSpan={4} className="border border-slate-500" />
                </tr>
              </tfoot>
            </table>

            {tagihanList.some((t) => !t.bulan) && (
              <div className="mt-4">
                <p className="text-xs font-bold mb-1">Pos Pembayaran Lainnya</p>
                <table className="w-full text-xs border border-slate-500 border-collapse">
                  <thead>
                    <tr className="bg-slate-100 font-bold text-center">
                      <th className="border border-slate-500 py-1 px-2 text-left">Pos</th>
                      <th className="border border-slate-500 py-1 px-2 text-right">Nominal</th>
                      <th className="border border-slate-500 py-1 px-2 text-right">Terbayar</th>
                      <th className="border border-slate-500 py-1 px-2 text-right">Sisa</th>
                      <th className="border border-slate-500 py-1 px-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tagihanList
                      .filter((t) => !t.bulan)
                      .map((t) => (
                        <tr key={t.id}>
                          <td className="border border-slate-500 px-2 font-semibold">
                            {t.jenisPembayaran?.nama || 'Pos Pembayaran'}
                          </td>
                          <td className="border border-slate-500 px-2 text-right font-mono">
                            {Number(t.nominal).toLocaleString('id-ID')}
                          </td>
                          <td className="border border-slate-500 px-2 text-right font-mono">
                            {Number(t.terbayar).toLocaleString('id-ID')}
                          </td>
                          <td className="border border-slate-500 px-2 text-right font-mono">
                            {Number(t.sisa).toLocaleString('id-ID')}
                          </td>
                          <td className="border border-slate-500 px-2 text-center font-bold">
                            {t.status === 'lunas' ? 'LUNAS' : t.status === 'sebagian' ? 'KURANG' : 'BELUM'}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="grid grid-cols-2 gap-8 text-center text-xs mt-6">
              <div>
                <p>Orang Tua / Wali Santri</p>
                <div className="h-14" />
                <p className="font-bold border-t border-slate-500 inline-block px-8">( ................................ )</p>
              </div>
              <div>
                <p>Bendahara</p>
                <div className="h-14" />
                <p className="font-bold border-t border-slate-500 inline-block px-8">( ................................ )</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Lembar Cetak Kwitansi Gabungan (hanya dirender saat mencetak) */}
      {printStruk && struk && (
        <>
          <style>{`
            @media print {
              @page { size: A5 landscape; margin: 10mm; }
              html, body, #root { height: auto !important; overflow: visible !important; background: #fff !important; }
              body * { visibility: hidden !important; }
              .struk-gabungan-sheet, .struk-gabungan-sheet * { visibility: visible !important; }
              .struk-gabungan-sheet { position: absolute !important; left: 0 !important; top: 0 !important; width: 100% !important; }
              .struk-gabungan-sheet tr { break-inside: avoid; page-break-inside: avoid; }
            }
          `}</style>
          <div className="struk-gabungan-sheet hidden print:block text-slate-900">
            <div className="border-b-2 border-slate-800 pb-2 mb-3">
              <div className="flex items-center gap-3">
                {profilMadrasah?.logo_url && (
                  <img src={profilMadrasah.logo_url} alt="Logo" className="w-12 h-12 object-contain shrink-0" />
                )}
                <div className="flex-1 text-center">
                  <h2 className="text-sm font-extrabold uppercase tracking-tight">
                    {profilMadrasah?.nama || 'Madrasah'}
                  </h2>
                  <p className="text-[9px]">
                    {profilMadrasah?.alamat || ''}
                    {profilMadrasah?.telepon ? ` • Telp: ${profilMadrasah.telepon}` : ''}
                  </p>
                </div>
              </div>
              <h1 className="text-base font-extrabold uppercase tracking-wider text-center mt-2">
                KWITANSI PEMBAYARAN
              </h1>
              <p className="text-[10px] text-center font-mono">
                No: {struk.nomor.join(', ') || '-'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs mb-3">
              <div>Nama: <strong>{struk.siswaNama}</strong></div>
              <div>Tanggal: <strong>{struk.tanggal}</strong></div>
              <div>NIS / Kelas: <strong>{struk.nis} / {struk.kelas}</strong></div>
              <div>Metode: <strong>{struk.metode}</strong></div>
            </div>

            <table className="w-full text-xs border border-slate-500 border-collapse">
              <thead>
                <tr className="bg-slate-100 font-bold">
                  <th className="border border-slate-500 py-1 px-2 w-8 text-center">No</th>
                  <th className="border border-slate-500 py-1 px-2 text-left">Uraian Pembayaran</th>
                  <th className="border border-slate-500 py-1 px-2 text-right">Jumlah</th>
                </tr>
              </thead>
              <tbody>
                {struk.rows.map((r, i) => (
                  <tr key={i} className="h-6">
                    <td className="border border-slate-500 px-2 text-center">{i + 1}</td>
                    <td className="border border-slate-500 px-2">
                      {r.nama} {r.bulan && `(${r.bulan})`}
                    </td>
                    <td className="border border-slate-500 px-2 text-right font-mono">
                      {rp(r.jumlah)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 font-extrabold">
                  <td colSpan={2} className="border border-slate-500 px-2 py-1 text-right">TOTAL</td>
                  <td className="border border-slate-500 px-2 text-right font-mono">{rp(struk.total)}</td>
                </tr>
              </tfoot>
            </table>

            {struk.catatan && <p className="text-[10px] mt-2">Catatan: {struk.catatan}</p>}

            <div className="grid grid-cols-2 gap-8 text-center text-xs mt-4">
              <div>
                <p>Penyetor</p>
                <div className="h-12" />
                <p className="font-bold border-t border-slate-500 inline-block px-8">( ............................ )</p>
              </div>
              <div>
                <p>Bendahara</p>
                <div className="h-12" />
                <p className="font-bold border-t border-slate-500 inline-block px-8">( ............................ )</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Kwitansi Printable Modal */}
      <KwitansiModal
        isOpen={selectedTxId !== null}
        onClose={() => setSelectedTxId(null)}
        transaksiId={selectedTxId}
      />
    </div>
  );
}
